import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import AnimatedBackground from '../components/AnimatedBackground';
import { fireEvent } from '../utils/funnelEvents';
import ArticleStructuredData from '../components/ArticleStructuredData';
import VerticalHeader from '../components/VerticalHeader';

import PropertyHero from './property-management/PropertyHero';
import Workflow from './property-management/Workflow';
import PainPoints from './property-management/PainPoints';
import DocumentTypes from './property-management/DocumentTypes';
import Integrations from './property-management/Integrations';
import RelatedReading from './property-management/RelatedReading';
import VerticalPricing from './shared/VerticalPricing';
import PropertyFAQ from './property-management/PropertyFAQ';
import VerticalContact from './shared/VerticalContact';
import ResearchInviteCard from '../components/research/ResearchInviteCard';

const PAGE_TITLE = 'AI File Organizer for Property Management (Buildium, Rent Manager) | Sortio';
const PAGE_DESCRIPTION =
  'Sortio organizes per-unit folders for property managers. Auto-routes leases, maintenance reports, vendor invoices, owner statements, and tenant ledgers. Buildium and Rent Manager integrations shipping. Runs on Mac and Windows.';

const NAV = [
  { href: '#workflow', label: 'How it works' },
  { href: '#document-types', label: 'Document types' },
  { href: '#integrations', label: 'Integrations' },
  { href: '#pricing', label: 'Pricing' },
];

const PropertyManagementPage = () => {
  useEffect(() => {
    fireEvent('team_page_view', { vertical: 'property-management' });
  }, []);

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta
          name="keywords"
          content="property management file organization, Buildium document organization, Rent Manager file automation, per-unit folder organization, lease document management, tenant ledger filing, multifamily document workflow, HOA file management"
        />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.getsortio.com/for-property-management" />
        <link rel="canonical" href="https://www.getsortio.com/for-property-management" />
      </Helmet>
      <ArticleStructuredData
        title={PAGE_TITLE}
        description={PAGE_DESCRIPTION}
        datePublished="2026-05-14T00:00:00.000Z"
        dateModified="2026-05-14T00:00:00.000Z"
        authorName="Sortio Team"
      />

      <VerticalHeader verticalLabel="Property Mgmt" navItems={NAV} />
      <PropertyHero />

      <div className="relative">
        <AnimatedBackground variant="business" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
          <Workflow />
          <PainPoints />
          <DocumentTypes />
          <Integrations />
          <RelatedReading />
          <VerticalPricing
            verticalLabel="Property Management"
            description="Independent PMs and small operators start with Pro. Multifamily operators and HOA management companies graduate to Team for shared rules across leasing agents, maintenance coordinators, and bookkeepers."
            proDescription="For independent and small PM operators"
            proFeatureLabel="Per-unit folder routing"
          />
          <PropertyFAQ />
          <ResearchInviteCard vertical="property-management" />
          <VerticalContact
            waitlistType="property-management"
            body="Tell us about your portfolio and we will show you Sortio working with a sample of your units."
            orgLabel="Company Name"
            orgPlaceholder="Acme Property Mgmt"
            sizeLabel="Units Under Management"
            sizeOptions={[
              { value: '1-50', label: '1-50 units' },
              { value: '51-200', label: '51-200 units' },
              { value: '201-1000', label: '201-1,000 units' },
              { value: '1000+', label: '1,000+ units' },
            ]}
          />
        </div>
      </div>
    </>
  );
};

export default PropertyManagementPage;
